import { useEffect, useRef, useState } from 'react';
import { TURNSTILE_SCRIPT_ID } from './contactFormShared';

type TurnstileStatus = 'idle' | 'loading' | 'ready' | 'error';

export default function useContactTurnstile(theme: 'light' | 'dark' = 'light') {
  const turnstileSiteKey = String(import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();
  const [turnstileStatus, setTurnstileStatus] = useState<TurnstileStatus>(turnstileSiteKey ? 'loading' : 'idle');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const turnstileHostRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!turnstileSiteKey) return;

    let cancelled = false;
    let attachedLoadHandler: (() => void) | null = null;

    const renderWidget = () => {
      if (cancelled || !turnstileHostRef.current || !window.turnstile) return;

      try {
        if (turnstileWidgetIdRef.current) {
          window.turnstile.remove(turnstileWidgetIdRef.current);
          turnstileWidgetIdRef.current = null;
        }

        turnstileWidgetIdRef.current = window.turnstile.render(turnstileHostRef.current, {
          sitekey: turnstileSiteKey,
          theme,
          callback: (token) => {
            if (cancelled) return;
            setTurnstileToken(token);
            setTurnstileError('');
            setTurnstileStatus('ready');
          },
          'expired-callback': () => {
            if (cancelled) return;
            setTurnstileToken('');
            setTurnstileError('');
            setTurnstileStatus('ready');
          },
          'error-callback': () => {
            if (cancelled) return;
            setTurnstileToken('');
            setTurnstileStatus('error');
            setTurnstileError('The security check could not load. Refresh the page and try again.');
          },
        });

        setTurnstileError('');
        setTurnstileStatus('ready');
      } catch {
        if (!cancelled) {
          setTurnstileStatus('error');
          setTurnstileError('The security check could not load. Refresh the page and try again.');
        }
      }
    };

    if (window.turnstile) {
      renderWidget();

      return () => {
        cancelled = true;
        if (turnstileWidgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(turnstileWidgetIdRef.current);
          } catch {}
        }
        turnstileWidgetIdRef.current = null;
      };
    }

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existingScript) {
      attachedLoadHandler = () => renderWidget();
      existingScript.addEventListener('load', attachedLoadHandler, { once: true });
    } else {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      script.onerror = () => {
        if (!cancelled) {
          setTurnstileStatus('error');
          setTurnstileError('The security check could not load. Refresh the page and try again.');
        }
      };
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (existingScript && attachedLoadHandler) {
        existingScript.removeEventListener('load', attachedLoadHandler);
      }
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch {}
      }
      turnstileWidgetIdRef.current = null;
    };
  }, [theme, turnstileSiteKey]);

  function resetTurnstileWidget() {
    setTurnstileToken('');

    if (!turnstileSiteKey || !turnstileWidgetIdRef.current || !window.turnstile) {
      return;
    }

    try {
      window.turnstile.reset(turnstileWidgetIdRef.current);
      setTurnstileError('');
      setTurnstileStatus('ready');
    } catch {
      setTurnstileStatus('error');
      setTurnstileError('The security check could not load. Refresh the page and try again.');
    }
  }

  return {
    turnstileError,
    turnstileHostRef,
    turnstileSiteKey,
    turnstileStatus,
    turnstileToken,
    resetTurnstileWidget,
  };
}
